import useClosestLocation from "@/hooks/useClosestLocation";
import { useAppSelector } from "@/store";
import { updateCinemaHall } from "@/store/slices";
import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { Image, OptionModal } from "@picovico/ui";
import {
  getLocalStorageData,
  getSessionStorageData,
  storeLocalStorageData,
  storeSessionStorageData,
} from "@picovico/utils";
import React, { useCallback, useEffect } from "react";
import { GiTheater } from "react-icons/gi";
import { IoLocationSharp } from "react-icons/io5";
import { useDispatch } from "react-redux";

export interface BannerPopUpWrapperProps {
  children: React.ReactNode;
}

function BannerPopUpWrapper({ children }: BannerPopUpWrapperProps) {
  const { layout_config } = useAppSelector((state) => state.config);
  const { cinema_hall, isOpen } = useAppSelector(
    (state) => state.activeCinemaHall,
  );
  const dispatch = useDispatch();
  const onOpen = useCallback(() => {
    dispatch(
      updateCinemaHall({
        isOpen: true,
      }),
    );
  }, [dispatch]);
  const onClose = useCallback(() => {
    dispatch(
      updateCinemaHall({
        isOpen: false,
      }),
    );
  }, [dispatch]);

  const cinemaHalls = useAppSelector((state) => state.config.cinema_halls);
  const {
    isOpen: isReminderOpen,
    onOpen: onReminderOpen,
    onClose: onReminderClose,
  } = useDisclosure();
  const { closestLocations, error } = useClosestLocation(cinemaHalls || []);
  error && console.warn("geolocationerror", error);

  useEffect(() => {
    const storedCinemaHall = getLocalStorageData("cinema_hall");
    const isValidStoredHall =
      storedCinemaHall &&
      storedCinemaHall !== "null" &&
      cinemaHalls.some((hall) => hall.idx === storedCinemaHall);

    if (isValidStoredHall) {
      dispatch(updateCinemaHall({ cinema_hall: storedCinemaHall }));
      return;
    } else if (cinemaHalls?.length === 1) {
      const defaultHall = cinemaHalls[0].idx;
      dispatch(updateCinemaHall({ cinema_hall: defaultHall }));
      storeLocalStorageData("cinema_hall", defaultHall);
      return;
    } else {
      onOpen();
    }
  }, [onOpen, cinemaHalls, dispatch, cinema_hall]);

  useEffect(() => {
    if (getSessionStorageData("popUp")) return;
    onReminderOpen();
    storeSessionStorageData("popUp", "true");
  }, [onReminderOpen]);

  return (
    <>
      <OptionModal
        titleIcon={<IoLocationSharp />}
        title="Please Select Theater"
        isOpen={isOpen && cinemaHalls.length > 1}
        subTitle="Select the cinema hall near your location"
        onClose={onClose}
        onSelection={(selection) => {
          dispatch(updateCinemaHall({ cinema_hall: selection }));
          storeLocalStorageData("cinema_hall", selection);
          onClose();
        }}
        isRequired
        isCentered
        options={
          (closestLocations.length > 0 ? closestLocations : cinemaHalls)?.map(
            (i) => ({
              title: i.name,
              value: i.idx,
              icon: GiTheater,
            }),
          ) || []
        }
      />
      {layout_config?.popup_image && (
        <Modal
          isOpen={isReminderOpen}
          onClose={onReminderClose}
          isCentered
          size="custom"
        >
          <ModalOverlay bg="blackAlpha.800" />
          <ModalContent
            maxW="fit-content"
            minW={"fit-content"}
            bg="transparent"
            boxShadow="none"
            m={0}
            p={0}
          >
            <ModalCloseButton
              size="sm"
              color="white"
              bg="primary.500"
              borderRadius="full"
              _hover={{ bg: "blackAlpha.800" }}
              position="absolute"
              top="-0.5rem"
              right="-0.5rem"
            />
            <Image
              src={layout_config?.popup_image}
              alt="Modal Image"
              maxH="80vh"
              maxW="80vw"
              objectFit="contain"
            />
          </ModalContent>
        </Modal>
      )}
      {children}
    </>
  );
}

export default BannerPopUpWrapper;
